// ===================================
// KAIXO - Perfil público de Canal V2
// Se puede mostrar sin estar suscrito.
// ===================================

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from '../Icons/Icons';
import ImageViewer from '../ImageViewer/ImageViewer';
import VideoViewer from '../ImageViewer/VideoViewer';
import LatidoCarousel from './LatidoCarousel';
import StoriesViewer from './StoriesViewer';
import ct from './i18n';
import { v2GetPublicProfile, v2Subscribe } from '../../services/api';
import InviteFollowersModal from './InviteFollowersModal';
import VerifiedBadge from '../VerifiedBadge/VerifiedBadge';
import VerificationModal from '../VerificationModal/VerificationModal';
import ReportModal from '../ReportModal/ReportModal';

function ChannelProfile({ inviteCode, currentUser, onClose, onSubscribed, onEdit }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [subscribed, setSubscribed] = useState(false);
    const [fullImg, setFullImg] = useState(null);
    const [fullVideo, setFullVideo] = useState(null);
    const [storiesOpen, setStoriesOpen] = useState(false);
    const [subscribing, setSubscribing] = useState(false);
    const [showInvite, setShowInvite] = useState(false);
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [reportingPost, setReportingPost] = useState(null);

    // Refresca los posts del perfil. Se llama al abrir y cuando se
    // dispara 'v2_post_published' (después de publicar un latido propio).
    const reloadProfile = React.useCallback(() => {
        if (!inviteCode) return;
        const lang = currentUser?.language || 'inglés';
        v2GetPublicProfile(inviteCode, lang)
            .then(({ data: d }) => { setData(d); setError(''); })
            .catch(e => setError(e.response?.data?.error || e.message));
    }, [inviteCode, currentUser?.language]);

    useEffect(() => {
        if (!inviteCode) return;
        setLoading(true);
        const lang = currentUser?.language || 'inglés';
        v2GetPublicProfile(inviteCode, lang)
            .then(({ data: d }) => { setData(d); setError(''); })
            .catch(e => setError(e.response?.data?.error || e.message))
            .finally(() => setLoading(false));
    }, [inviteCode, currentUser?.language]);

    // Listener de publicación: cuando el owner publica desde este perfil
    // (o cualquier otra vía), recargamos el feed del perfil para que
    // aparezca el post nuevo sin tener que salir y volver.
    useEffect(() => {
        const h = () => reloadProfile();
        window.addEventListener('v2_post_published', h);
        return () => window.removeEventListener('v2_post_published', h);
    }, [reloadProfile]);

    const handleSubscribe = async () => {
        if (!currentUser) return;
        setSubscribing(true);
        try {
            // Soporta @handle o inviteCode hex
            const id = String(inviteCode || '').trim();
            const body = id.startsWith('@')
                ? { handle: id, userId: currentUser._id }
                : { inviteCode: id, userId: currentUser._id };
            await v2Subscribe(body);
            setSubscribed(true);
            if (onSubscribed) onSubscribed();
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        }
        setSubscribing(false);
    };

    // Portal: renderizar en document.body para escapar del transform del parent en móvil
    const portal = (content) => ReactDOM.createPortal(content, document.body);

    if (loading) return portal(
        <div className="v2-profile-overlay" onClick={onClose}>
            <div className="v2-profile-modal" onClick={e => e.stopPropagation()}>
                <p className="v2-center">{ct.loading}</p>
            </div>
        </div>
    );

    if (error || !data?.channel) return portal(
        <div className="v2-profile-overlay" onClick={onClose}>
            <div className="v2-profile-modal" onClick={e => e.stopPropagation()}>
                <p className="v2-center" style={{ color: '#e53935' }}>{error || 'Not found'}</p>
                <button className="v2-btn-secondary v2-btn-full" onClick={onClose}>{ct.close}</button>
            </div>
        </div>
    );

    const ch = data.channel;
    const msgs = data.messages || [];
    const catLabel = ch.category ? (ct.categoryLabels?.[ch.category] || ch.category) : '';

    return portal(
        <div className="v2-profile-overlay" onClick={onClose}>
            <div className="v2-profile-modal" onClick={e => e.stopPropagation()}>
                {/* Header con avatar */}
                <div className="v2-profile-header">
                    <button className="v2-icon-btn" onClick={onClose} aria-label={ct.close}>
                        <Icons.Close size={18} color="white" />
                    </button>
                </div>
                <ImageViewer src={fullImg} onClose={() => setFullImg(null)} />
                <VideoViewer src={fullVideo} onClose={() => setFullVideo(null)} />
                {storiesOpen && data?.activeStories?.length > 0 && (
                    <StoriesViewer
                        groups={[{
                            channel: {
                                _id: data.channel?._id || data?._id,
                                name: ch.name,
                                avatar: ch.avatar,
                                inviteCode: ch.inviteCode,
                                handle: ch.handle
                            },
                            stories: data.activeStories
                        }]}
                        startGroupIdx={0}
                        onClose={() => setStoriesOpen(false)}
                    />
                )}
                <div className="v2-profile-avatar-wrap">
                    <div
                        className={`v2-profile-avatar ${data?.hasActiveStories ? 'v2-profile-avatar-has-stories' : ''}`}
                        onClick={() => {
                            // Si hay historias activas, tocar el avatar abre el visor de historias.
                            // Si no, abre la imagen del avatar a pantalla completa.
                            if (data?.hasActiveStories) {
                                setStoriesOpen(true);
                            } else if (ch.avatar) {
                                setFullImg(ch.avatar);
                            }
                        }}
                        style={(ch.avatar || data?.hasActiveStories) ? { cursor: 'pointer' } : {}}
                    >
                        {ch.avatar ? <img src={ch.avatar} alt="" /> : ch.name?.[0]?.toUpperCase()}
                    </div>
                    {currentUser && onEdit && ch.ownerName && ch.ownerName === currentUser.name && (
                        <button className="v2-profile-edit-btn" onClick={onEdit} aria-label="edit">
                            <Icons.Edit size={14} color="white" />
                        </button>
                    )}
                </div>

                {/* Info */}
                <div className="v2-profile-info">
                    <h2 className="v2-profile-name">
                        {ch.name}
                        {/* Insignia del CANAL primero (nuevo modelo).
                            Si no tiene canal-verified, fallback a owner-verified
                            (compat con cuentas legacy verificadas a nivel perfil). */}
                        {(ch.verifiedLevel || ch.ownerVerifiedLevel) && (
                            <VerifiedBadge level={ch.verifiedLevel || ch.ownerVerifiedLevel} />
                        )}
                    </h2>
                    {catLabel && <div className="v2-profile-cat">{catLabel}</div>}
                    {ch.description && <p className="v2-profile-desc">{ch.description}</p>}
                    {ch.bio && <p className="v2-profile-bio">{ch.bio}</p>}

                    <div className="v2-profile-stats">
                        <div className="v2-profile-stat">
                            <Icons.Users size={14} />
                            <span>{ch.subscribersCount} {ct.subscribers}</span>
                        </div>
                    </div>

                    {ch.links?.length > 0 && (
                        <div className="v2-profile-links">
                            {ch.links.map((link, i) => {
                                const clean = link.trim();
                                if (/^(javascript|data):/i.test(clean)) return null;
                                const url = /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
                                return (
                                    <button key={i} className="v2-profile-link" onClick={() => window.open(url, '_blank', 'noopener')}>
                                        <Icons.Link size={12} /> {clean.replace(/^https?:\/\//i, '').slice(0, 40)}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Acciones del owner justo bajo el header, antes del feed */}
                {(() => {
                    const chData = data?.channel || data;
                    const ownerId = chData?.ownerId;
                    const isOwner = currentUser?._id && ownerId && String(ownerId) === String(currentUser._id);
                    if (!isOwner) return null;
                    return (
                        <div className="v2-profile-publish-row">
                            <button
                                className="v2-btn-primary"
                                onClick={() => {
                                    window.dispatchEvent(new CustomEvent('open_publish_modal', {
                                        detail: { channelId: chData._id || data?._id, mode: 'latido' }
                                    }));
                                }}
                            >
                                {ct.publishLatido || 'Publicar latido'}
                            </button>
                            <button
                                className="v2-btn-secondary v2-btn-story"
                                onClick={() => {
                                    window.dispatchEvent(new CustomEvent('open_publish_modal', {
                                        detail: { channelId: chData._id || data?._id, mode: 'story' }
                                    }));
                                }}
                            >
                                {ct.publishStory || 'Publicar historia'}
                            </button>
                            <button
                                className="v2-btn-secondary"
                                onClick={() => setShowInvite(true)}
                            >
                                {ct.inviteFollowers || 'Invitar'}
                            </button>
                            {/* Verificación POR CANAL.
                                - Canal sin verificar → "Solicitar verificación"
                                - Canal basic/creator → "Evolucionar verificación"
                                - Canal business → no aparece (nivel máximo) */}
                            {(() => {
                                const channelLevel = chData?.verifiedLevel || chData?.ownerVerifiedLevel || null;
                                if (!channelLevel) {
                                    return (
                                        <button
                                            className="v2-btn-secondary"
                                            onClick={() => setShowVerifyModal(true)}
                                            style={{ borderColor: '#00b4d8', color: '#0096c7' }}
                                        >
                                            ✓ Solicitar verificación
                                        </button>
                                    );
                                }
                                if (channelLevel === 'basic' || channelLevel === 'creator') {
                                    return (
                                        <button
                                            className="v2-btn-secondary"
                                            onClick={() => setShowVerifyModal(true)}
                                            style={{ borderColor: '#6c5ce7', color: '#5a4fd1' }}
                                        >
                                            ↑ Evolucionar verificación
                                        </button>
                                    );
                                }
                                return null; // business = no upgrade
                            })()}
                        </div>
                    );
                })()}

                {/* Últimos anuncios — más reciente arriba */}
                {msgs.length > 0 && (
                    <div className="v2-profile-posts">
                        {[...msgs]
                            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                            .map(m => {
                                const chData = data?.channel || data;
                                const ownerId = chData?.ownerId;
                                const isOwner = currentUser?._id && ownerId && String(ownerId) === String(currentUser._id);
                                const isBusinessChannel = chData?.type === 'business';
                                return (
                            <div key={m._id} className="v2-profile-post">
                                {(() => {
                                    // Carrusel con múltiples medios (nuevo formato)
                                    if (Array.isArray(m.mediaUrls) && m.mediaUrls.length > 0) {
                                        return (
                                            <LatidoCarousel
                                                media={m.mediaUrls}
                                                onOpenImage={(url) => setFullImg(url)}
                                                onOpenVideo={(url) => setFullVideo(url)}
                                            />
                                        );
                                    }
                                    // Retrocompat: un solo medio antiguo
                                    if (m.mediaType === 'image' && m.mediaUrl) {
                                        return (
                                            <img
                                                src={m.mediaUrl}
                                                alt=""
                                                loading="lazy"
                                                decoding="async"
                                                className="v2-profile-post-img"
                                                onClick={() => setFullImg(m.mediaUrl)}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        );
                                    }
                                    if (m.mediaType === 'video' && m.mediaUrl) {
                                        return (
                                            <video
                                                src={m.mediaUrl}
                                                className="v2-profile-post-img"
                                                controls
                                                playsInline
                                                preload="metadata"
                                                onClick={() => setFullVideo(m.mediaUrl)}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        );
                                    }
                                    return null;
                                })()}
                                {m.text && <div className="v2-profile-post-text">{m.text}</div>}
                                <div className="v2-profile-post-time">{new Date(m.createdAt).toLocaleString()}</div>
                                {/* Acciones del owner sobre su propio post */}
                                {isOwner && (
                                    <div className="v2-profile-post-actions">
                                        <button
                                            className="v2-edit-btn"
                                            onClick={() => window.dispatchEvent(new CustomEvent('v2_edit_post', {
                                                detail: {
                                                    _id: m._id,
                                                    originalText: m.text || m.originalText || '',
                                                    visibility: m.visibility || 'subscribers',
                                                    contactMode: m.contactMode || '',
                                                    commentsEnabled: !!m.commentsEnabled,
                                                    interests: Array.isArray(m.interests) ? m.interests : [],
                                                    isBusiness: isBusinessChannel
                                                }
                                            }))}
                                        >
                                            <Icons.Edit size={13} /> Editar
                                        </button>
                                        {isBusinessChannel && (
                                            <button
                                                className="v2-boost-btn"
                                                onClick={() => window.dispatchEvent(new CustomEvent('v2_boost_post', {
                                                    detail: { message: m, channelId: chData._id || data?._id }
                                                }))}
                                            >
                                                <Icons.Star size={13} /> Impulsar
                                            </button>
                                        )}
                                        <button
                                            className="v2-edit-btn"
                                            style={{ color: '#c0392b', borderColor: '#c0392b' }}
                                            onClick={() => window.dispatchEvent(new CustomEvent('v2_delete_post', { detail: { _id: m._id } }))}
                                        >
                                            <Icons.Trash size={13} /> Eliminar
                                        </button>
                                    </div>
                                )}
                                {/* Denunciar publicación — solo a usuarios que NO son owner */}
                                {!isOwner && (
                                    <div className="v2-profile-post-actions">
                                        <button
                                            className="v2-edit-btn"
                                            onClick={() => setReportingPost({
                                                postId: m._id,
                                                authorId: chData?.ownerId || m.author,
                                                authorName: chData?.ownerName || '',
                                                snapshot: { text: m.text || '', mediaUrls: m.mediaUrls || [] }
                                            })}
                                            style={{ color: '#c0392b', borderColor: '#c0392b' }}
                                            aria-label="Denunciar publicación"
                                            title="Denunciar publicación"
                                        >
                                            ⚠ Denunciar
                                        </button>
                                    </div>
                                )}
                            </div>
                                );
                            })
                        }
                    </div>
                )}

                {/* CTA — solo para NO owner (el owner tiene sus acciones arriba) */}
                <div className="v2-profile-cta">
                    {(() => {
                        const chData = data?.channel || data;
                        const isOwner = currentUser?._id && chData?.ownerId
                            && String(chData.ownerId) === String(currentUser._id);
                        if (isOwner) return null;
                        if (subscribed) {
                            return (
                                <button className="v2-btn-primary v2-btn-full" disabled>
                                    <Icons.Check size={16} color="white" /> {ct.alreadySubscribed}
                                </button>
                            );
                        }
                        return (
                            <button className="v2-btn-primary v2-btn-full" onClick={handleSubscribe} disabled={subscribing || !currentUser}>
                                <Icons.Plus size={16} color="white" /> {subscribing ? ct.loading : ct.subscribeCta}
                            </button>
                        );
                    })()}
                </div>
            </div>
            {showInvite && (data?.channel?._id || data?._id) && (
                <InviteFollowersModal
                    channelId={data?.channel?._id || data?._id}
                    channelName={data?.channel?.name || data?.name || ''}
                    currentUserId={currentUser?._id}
                    onClose={() => setShowInvite(false)}
                />
            )}
            {showVerifyModal && (
                <VerificationModal
                    currentUser={currentUser}
                    channelId={data?.channel?._id || data?._id}
                    channelName={data?.channel?.name || data?.name}
                    channelVerifiedLevel={data?.channel?.verifiedLevel || data?.verifiedLevel || null}
                    onClose={() => setShowVerifyModal(false)}
                />
            )}
            {reportingPost && (
                <ReportModal
                    targetType="post"
                    targetId={reportingPost.postId}
                    targetUserId={reportingPost.authorId}
                    targetUserName={reportingPost.authorName}
                    snapshot={reportingPost.snapshot}
                    onClose={() => setReportingPost(null)}
                />
            )}
        </div>
    );
}

export default ChannelProfile;
